import { createLogger } from '@fabric-app/utils/logging';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import pLimit from 'p-limit';
import { v4 } from 'uuid';
import * as Y from 'yjs';
import { toast, confirm } from '../../../store/alerts.js';
import { ensureError } from '@fabric-app/utils/guards';
import { updateNodeAttributesWithResourceDetail } from './utils.js';
import { EMBED_RESOURCE_TYPE_NAME } from './constants.js';
import { getCompatEnv } from '@fabric-app/compat/capabilities/environment';

const logger = createLogger("Embed upload", true);
const MAX_CONCURRENT_UPLOADS = 5;
const MAX_UPLOADS = 15;
const findNode = (view, uuid) => {
  let found = null;
  view.state.doc.descendants((node, pos) => {
    if (node.attrs["data-uuid"] === uuid && node.type.name === EMBED_RESOURCE_TYPE_NAME) {
      found = {
        node,
        pos
      };
    }
    if (found) return false;
  });
  return found;
};
const updateNodeAttributes = (view, uuid, nextAttributes) => {
  try {
    const foundNode = findNode(view, uuid);
    if (!foundNode) return;
    const { node: { attrs, type }, pos: newPos } = foundNode;
    const updateTransaction = view.state.tr.setNodeMarkup(newPos, type, {
      ...attrs,
      ...nextAttributes
    }, []);
    view.dispatch(updateTransaction);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};
const handleMultipleEmbedUploads = async ({ files, ...rest }) => {
  if (files.length > MAX_UPLOADS) {
    toast({
      content: `You can only upload a maximum of ${MAX_UPLOADS} files at a time.`
    });
    return;
  }
  const concurrentLimit = pLimit(MAX_CONCURRENT_UPLOADS);
  return Promise.allSettled(files.map((file) => concurrentLimit(() => handleEmbedUpload({
    ...rest,
    file
  }))));
};
const handleEmbedUpload = async ({ onUpload, file, view, pos }) => {
  if (!onUpload) {
    toast({
      content: "Upload failed."
    });
    return;
  }
  const { isFabricDev } = getCompatEnv();
  let nodeId = null;
  try {
    logger.log("file", file);
    if (file.name.startsWith("__fabric_") && file.name.endsWith(".xml") && isFabricDev) {
      const ySync = view.state.plugins.find((plugin) => "key" in plugin && plugin.key === "y-sync$");
      const ySyncState = ySync?.getState(view.state);
      const doc = ySyncState?.doc;
      if (!doc || !(doc instanceof Y.Doc)) {
        return;
      }
      const confirmed = await confirm({
        content: "This will overwrite the current notepad. Are you sure you want to continue?",
        title: "Overwrite Notepad",
        cancelLabel: "Cancel",
        confirmLabel: "Overwrite"
      });
      if (!confirmed) return;
      const fragment = doc.getXmlFragment("default");
      const fileText = await file.text();
      const fixedText = fileText.replace(/&(?!\w*;)/g, "&amp;");
      const text = `<root>${fixedText}</root>`;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      const schemaNodeNames = Object.keys(view.state.schema.nodes);
      const addNode = (parent, element) => {
        if (element instanceof Element) {
          if (element.tagName === "root") {
            const children2 = Array.from(element.childNodes);
            for (const child of children2) {
              addNode(parent, child);
            }
            return;
          }
          const tagName = schemaNodeNames.find((name) => name.toLowerCase() === element.tagName.toLocaleLowerCase()) ?? element.tagName;
          const xmlElement = new Y.XmlElement(tagName);
          for (const attribute of element.attributes) {
            xmlElement.setAttribute(attribute.name, attribute.value);
          }
          const children = Array.from(element.childNodes);
          for (const child of children) {
            addNode(xmlElement, child);
          }
          parent.push([
            xmlElement
          ]);
        } else if (element instanceof Text) {
          if (!(parent instanceof Y.XmlElement)) return;
          const text2 = new Y.XmlText(element.textContent ?? "");
          parent.push([
            text2
          ]);
        }
      };
      const root = xmlDoc.querySelector("root");
      if (!root) return;
      doc.transact(() => {
        fragment.delete(0, fragment.length);
        addNode(fragment, root);
      }, "override");
      return;
    }
    const tempSrc = URL.createObjectURL(file);
    const { schema } = view.state;
    const node = schema.nodes[EMBED_RESOURCE_TYPE_NAME].create({
      mimeType: file.type,
      loading: true,
      alt: file.name,
      resourceId: void 0,
      parentResourceId: void 0,
      src: tempSrc,
      uploadProgress: 0,
      ["data-uuid"]: v4()
    });
    nodeId = node.attrs["data-uuid"];
    if (!nodeId) {
      const transaction = view.state.tr.deleteRange(pos, pos + node.nodeSize);
      view.dispatch(transaction);
      return;
    }
    const createTransaction = view.state.tr.insert(pos, node);
    try {
      view.dispatch(createTransaction);
    } catch (e) {
      captureException(ensureError(e));
      return;
    }
    logger.log("Start uploading");
    const result = await onUpload({
      file,
      onProgressUpdate: (progress) => {
        logger.log("Uploading progress:", progress);
        updateNodeAttributes(view, nodeId, {
          uploadProgress: progress
        });
      }
    });
    if (!result) throw new Error("There was an error uploading the file.");
    logger.log("Finished, updating node. Upload result:", result);
    updateNodeAttributesWithResourceDetail(view, nodeId, result.resource);
    result.ackProcessed();
  } catch (e) {
    captureException(ensureError(e));
    toast({
      content: "There was an error uploading the file."
    });
    if (!nodeId) return;
    const foundNode = findNode(view, nodeId);
    if (!foundNode) return;
    const { node, pos: pos2 } = foundNode;
    const transaction = view.state.tr.deleteRange(pos2, pos2 + node.nodeSize);
    view.dispatch(transaction);
  }
};

export { handleEmbedUpload, handleMultipleEmbedUploads };
//# sourceMappingURL=handleUpload.js.map
